import { Popover, Transition } from '@headlessui/react';
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';
import { useAuth } from '../../../../../scripts/fb';
import { UserAvatar } from '../../user-avatar';
import { getLocalStorage, setLocalStorage } from '../../../utils';
import {
  useAddToGroupList,
  usePageSelected,
  useShowModalEmpty,
  useShowNoSavedChangesModal,
  useStoreAllGridChanges,
  useStoreGroupedViews
} from '../../../lib/store/settings.store';
import { useAnalyticsEventTracker } from '../../Layout/app/frame/shared/analytics';
import {
  useAccountHasFreePlan,
  useAccountHasPlanPaid,
  useAccountHasStarterPlan,
  useAccountHasTrialPlanPaid,
  useIsAppDown,
  useShowErrorModalComingSoon
} from '../../../lib/store/dashboard.store';
import { useAccountHasLillyPaid } from '../../../lib/store/frame.store';
import { useMutation } from 'react-query';
import axios from 'axios';
import { ModalError } from '../modal/Error';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNewLillyClaim, useShowManageAccount } from '../../../lib/store/v3/layout';
import { Userpilot } from 'userpilot';
import { Loader } from '../loader';

export const SignOutPopover = ({
  openPopover,
  setOpenPopover,
  from
}: {
  openPopover: boolean;
  setOpenPopover: Dispatch<SetStateAction<boolean>>;
  from: string;
}) => {
  const accountPaidDashboard = useAccountHasPlanPaid((state: any) => state.accountPaid);
  const trialAccountPaid = useAccountHasTrialPlanPaid((state: any) => state.trialAccountPaid);
  const showStripeLink = useShowManageAccount((state: any) => state.showStripeLink);
  const accountLillyPaid = useAccountHasLillyPaid((state: any) => state.accountPaid);
  const starterAccount = useAccountHasStarterPlan((state: any) => state.accountPaid);
  const isAppDown = useIsAppDown((state: any) => state.isAppDown);
  const auth = useAuth();
  const [signText, setSignText] = useState<string>('Sign out');
  const domain = window.location.origin;
  const isUat = domain.includes('uat') || domain.includes('localhost');
  const newLillyClaim = useNewLillyClaim((state: any) => state.newLillyClaim);
  const setShowStripeLink = useShowManageAccount((state: any) => state.setShowStripeLink);
  const freeAccount = useAccountHasFreePlan((state: any) => state.freeAccount);
  const [options, setOptions] = useState<any>([
    {
      iconPath: null,
      option: 1,
      email: auth?.user?.email
    },
    {
      name: null,
      loading: true,
      iconPath: null,
      option: 3
    },
    {
      name: 'Manage Account',
      isLink: true,
      link: isUat
        ? 'https://billing.stripe.com/p/login/test_aEU0248Ohbrf5HieUU'
        : 'https://billing.stripe.com/p/login/fZe5mHdGCgCEaXudQQ',
      option: 4
    },
    {
      name: signText,
      iconPath: 'sign-out',
      option: 2
    }
  ]);
  const gridChanges = useStoreAllGridChanges((state: any) => state.gridChanges);
  const groupedViews = useStoreGroupedViews((state: any) => state.groupedViews);
  const groupRollbackList = useAddToGroupList((state: any) => state.groupRollbackList);
  const setShowModalEmpty = useShowModalEmpty((state: any) => state.setShowModalEmpty);
  const [leavePopover, setLeavePopover] = useState<boolean>(false);
  const setShowModal = useShowNoSavedChangesModal((state: any) => state.setShowModal);
  const gaEventTracker = useAnalyticsEventTracker('Header');
  const [sideBottom, setSideBottom] = useState<boolean>(false);
  const page = usePageSelected((state: any) => state.page);
  const setShowModalError = useShowErrorModalComingSoon((state: any) => state.setShowModalError);
  const location = useLocation().pathname;
  const navigate = useNavigate();

  useEffect(() => {
    const shouldRemoveStripeLink = !showStripeLink || accountLillyPaid || newLillyClaim;
    const hasStripeLinkOption = options.some((e) => e.option === 4);
    if (shouldRemoveStripeLink && hasStripeLinkOption) {
      const res = options.filter((e) => e.option !== 4);
      setOptions(res);
    } else if (showStripeLink && !hasStripeLinkOption && !shouldRemoveStripeLink) {
      const newOptionsList = [...options];
      const signOutOption = newOptionsList.pop();
      setOptions([
        ...newOptionsList,
        {
          name: 'Manage Account',
          isLink: true,
          link: isUat
            ? 'https://billing.stripe.com/p/login/test_aEU0248Ohbrf5HieUU'
            : 'https://billing.stripe.com/p/login/fZe5mHdGCgCEaXudQQ',
          option: 4
        },
        signOutOption
      ]);
    }
  }, [showStripeLink, accountLillyPaid, newLillyClaim, options]);

  useEffect(() => {
    const updatedOptions = [...options];
    const accountTypeOption = updatedOptions.find((opt) => opt.option === 3);
    if (accountTypeOption && !accountTypeOption.name) {
      accountTypeOption.name = isAppDown
        ? 'Account Type: '
        : accountLillyPaid || newLillyClaim
        ? 'Account Type: Business'
        : trialAccountPaid && !freeAccount
        ? 'Account Type: Pro Free Trial'
        : accountPaidDashboard
        ? 'Account Type: Pro'
        : starterAccount
        ? 'Account Type: Lite'
        : freeAccount
        ? 'Account Type: Free'
        : null;

      accountTypeOption.loading =
        !isAppDown &&
        !accountLillyPaid &&
        !newLillyClaim &&
        !trialAccountPaid &&
        !accountPaidDashboard &&
        !starterAccount &&
        !freeAccount;

      setOptions(updatedOptions);
    }
  }, [
    isAppDown,
    accountLillyPaid,
    newLillyClaim,
    trialAccountPaid,
    freeAccount,
    accountPaidDashboard,
    starterAccount,
    showStripeLink
  ]);

  const hasUnsaveSettingsChanges = () => {
    const hasRollBackSettings =
      gridChanges?.length > 0 ||
      groupedViews?.length > 0 ||
      (groupRollbackList && Object.entries(groupRollbackList)?.length > 0);

    return hasRollBackSettings;
  };

  const signOut = (option: number) => {
    setShowStripeLink(true);
    if (option === 2) {
      const hasRollBackSettings = hasUnsaveSettingsChanges();
      gaEventTracker('Sign out');
      const emailStorage = getLocalStorage('email-storage');
      const passStorage = getLocalStorage('password-storage');
      const checkedRemember = getLocalStorage('remember-storage');
      const tokenTrialPeriod = getLocalStorage('start-trial-period-token') ?? null;
      const alreadyCheckExpirationFreeV: any = getLocalStorage('trial-expired-modal');
      localStorage.clear();
      if (tokenTrialPeriod) setLocalStorage('start-trial-period-token', tokenTrialPeriod);
      if (checkedRemember) {
        setLocalStorage('email-storage', emailStorage);
        setLocalStorage('password-storage', passStorage);
        setLocalStorage('remember-storage', true);
      }
      if (alreadyCheckExpirationFreeV == false) {
        setLocalStorage('trial-expired-modal', false);
      }
      localStorage.removeItem('projects-number');
      if (hasRollBackSettings) {
        setShowModalEmpty(true);
        setSignText('Sign out ...');
        setLeavePopover(true);
        setShowModal(true);
        setTimeout(() => {
          setLocalStorage('lastPageStored', 'settings');
        }, 2000);
      } else {
        // const workspace = location.split('/');
        setLocalStorage('token', null);
        auth.signout();
        setOpenPopover(false);
        setLeavePopover(false);
        setLocalStorage('userData', null);
        navigate('/sign_in');
        navigate(0);
      }
    }
  };

  const handleLeavePopover = () => {
    if (leavePopover) {
      setSignText('Sign out ...');
    } else setOpenPopover(false);
  };

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1080) {
        setSideBottom(true);
      } else {
        setSideBottom(false);
      }
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getPortalSession = useMutation(async (payload: any) => {
    const token = await auth.user?.getIdToken();
    if (!token) return;
    const route = `/portal_session`;
    try {
      const response = await axios.post(
        route,
        {
          return_url: payload.domain
        },
        {
          headers: {
            Authorization: token
          }
        }
      );
      if (response && response.status === 200) {
        window.open(response.data.url);
      }
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status == 403) {
        setShowModalError(true);
      }
    }
  });

  const handleLink = () => {
    const payload = {
      domain
    };
    getPortalSession.mutate(payload);
  };

  return (
    <Popover
      style={{ zIndex: 1000 }}
      className="relative mt-1 z-[1000]"
      onMouseLeave={handleLeavePopover}
      data-testid="popover-container">
      <>
        <Transition
          show={openPopover}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1">
          <Popover.Panel
            className={`absolute z-[1000] -translate-x-1 transform w-auto top-0 right-6`}>
            <div className="overflow-hidden rounded-[20px] ring-1 ring-black ring-opacity-5 shadow-xl border-[#788AA6] border-solid border-[1px]">
              <div className="grid grid-cols-1 divide-y-[1px] font-montserrat">
                {options.map((item, index) => (
                  <div
                    data-testid={`option-${index + 1}`}
                    onClick={() => signOut(item.option)}
                    key={item.option}
                    className={`${index === 3 ? 'hover:bg-[#F5F5F5]' : 'pointer-events-auto'} ${
                      leavePopover ? 'pointer-events-none' : ''
                    } ${index === 0 ? 'bg-[#F5F5F5]' : 'bg-white'} ${
                      index == 3 ? 'cursor-pointer' : ''
                    } flex items-center transition duration-150 ease-in-out p-2`}>
                    <div
                      className={`${
                        index == 0 ? 'ml-10 text-white' : ''
                      } h-9 w-9 flex items-center justify-center`}>
                      {item.iconPath ? (
                        <img
                          src={`/icons/${item.iconPath}.svg`}
                          alt={`${item.name} icon`}
                          className="-mr-3"
                        />
                      ) : item.option == 1 ? (
                        <div className="flex items-center">
                          <UserAvatar />
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                    {item.email ? (
                      <div className="flex flex-col -ml-[15px] mr-8 ">
                        <p className="text-[#364152] text-xs font-semibold">{item.email}</p>
                      </div>
                    ) : (
                      <div className="text-start flex justify-start">
                        {' '}
                        {item.loading ? (
                          <div className="-ml-[22px] flex items-center justify-center">
                            <span className="text-xs light-black font-semibold">Account Type:</span>
                            {/* <Loader showText={false} className="-mt-8 w-8 h-8 ml-3.5" /> */}
                          </div>
                        ) : (
                          <p
                            onClick={() => {
                              if (item.isLink) {
                                Userpilot.track('manage_account');
                                handleLink();
                              }
                            }}
                            className={`${
                              item.option == 3 || item.option == 4 ? '-ml-[22px]' : ' ml-1.5'
                            } text-xs font-semibold ${
                              item.isLink
                                ? 'underline underline-offset-4 text-[#2071EF] cursor-pointer'
                                : 'text-[#364152]'
                            }`}>
                            {item.name}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </>
    </Popover>
  );
};
